import React from "react";
import { rootStore, RootStoreContext } from "./stores";
import { App } from "./containers/App";
import { ConfirmModal } from "./elements/ConfirmModal/ConfirmModal";
import { ThemeProvider } from "styled-components";
import { initI18n } from "./locales/i18n";
import { EngineClientProvider } from "./services/engineClient";
import type { ApplicationModel } from "./types/application";
import type { UserPreferences } from "./types/user-preferences";
import { DEFAULT_USER_PREFERENCES } from "./defaults/user-preferences";
import { buildDefaultSolution } from "./defaults/solution";
import type { SolutionModel } from "./types/solution";
import type { Workspace } from "./types/workspace";
import { DEFAULT_WORKSPACE } from "./defaults/workspace";
import { LimitsClientProvider } from "./services/limitsClient";
import { UcesFlagsClientProvider } from "./services/ucesFlagsClient";
import { BrowserRouter } from "react-router-dom";
import { configure } from "mobx";
import type { SolutionColor } from "@smartsuite/types/lib/constants/color";
import { initSentry } from "./sentry";
import { ErrorModal } from "./elements/ErrorModal/ErrorModal";

configure({
  enforceActions: "never",
});

export interface AutomationUiProps {
  accountId: string;
  // @deprecated: please use solution.id
  solutionId: string;
  solution?: SolutionModel;
  solutions?: SolutionModel[];
  // for demo only , undefined on monolith
  accessToken?: string;
  engineUrl: string;
  limitsUrl: string;
  lang: string;
  logoColor: SolutionColor;
  // for demo only , undefined on monolith
  smartsuiteBackendUrl?: string;
  // applications can be set by monolith only
  applications?: ApplicationModel[];
  // selected application id
  applicationId?: string;
  userPreferences?: UserPreferences;
  workspace?: Workspace;
  onClose?: () => void;
  originRoute?: string;
}

export const AutomationUI: React.FC<AutomationUiProps> = (props) => {
  rootStore.init(
    props.accountId,
    props.solutionId,
    props.engineUrl,
    props.limitsUrl ?? process.env.REACT_APP_LIMITS_URL ?? "",
    props.accessToken ?? "",
    props.smartsuiteBackendUrl ?? process.env.REACT_APP_SS_BACKEND_URL ?? "",
    props.applications,
    props.applicationId,
    props.userPreferences ?? DEFAULT_USER_PREFERENCES,
    props.solution ?? buildDefaultSolution(props.solutionId),
    props.solutions,
    props.workspace ?? DEFAULT_WORKSPACE,
    props.originRoute ?? window.location.pathname,
  );

  const originRoute = props.originRoute ?? window.location.pathname;

  return (
    <React.StrictMode>
      <RootStoreContext.Provider value={rootStore}>
        <ThemeProvider theme={rootStore.theme}>
          <LimitsClientProvider>
            <EngineClientProvider>
              <UcesFlagsClientProvider>
                <BrowserRouter basename={originRoute}>
                  <App {...props} />
                  <ConfirmModal />
                  <ErrorModal />
                </BrowserRouter>
              </UcesFlagsClientProvider>
            </EngineClientProvider>
          </LimitsClientProvider>
        </ThemeProvider>
      </RootStoreContext.Provider>
    </React.StrictMode>
  );
};

// NOTE: when you add process.env.REACT_APP_... var here please don't forget to add it to build.js
initSentry({
  tracingEnabled: !!process.env.REACT_APP_SENTRY_ENVIRONMENT,
  sentryDSN: process.env.REACT_APP_SENTRY_DSN,
  envName: process.env.REACT_APP_SENTRY_ENVIRONMENT,
  tracingRelease: `${process.env.REACT_APP_NAME ?? ""}@${
    process.env.REACT_APP_VERSION ?? ""
  }`,
  sentryProfilingEnabled: true,
  sentryReplayEnabled: process.env.NODE_ENV === "production",
  sentryTracesSampleRate: process.env.NODE_ENV === "production" ? 0.5 : 1.0,
  sentryReplaysOnErrorSampleRate: 1.0,
  sentryReplaysSessionSampleRate: 0.1,
  sentryIgnoreErrors:
    process.env.REACT_APP_SENTRY_IGNORE_ERRORS?.split(",") ?? [],
});

initI18n();
